import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ApiService from "../../../../../service/ApiService";
import { apiPaths } from "../../../../../service/apiPaths";
import { MoveLeft } from "lucide-react";
import Modal from "../../../../../components/Model";

type Campaign = {
  _id: string;
  userId?: { _id: string; name: string; email: string } | string;
  campaignId?: string | { _id: string; name?: string };
  leadName?: string;
  companyName?: string;
  email?: string;
  services?: string;
  leadStatus?: string;
  companyWebsite?: string;
  linkedInLink?: string;
  description?: string;
  linkedInLeadStatus?: string;
  instagramLink?: string;
  leadWebLink?: string;
  personalization?: string;
  follow1?: string;
  Follow1?: string;
  follow2?: string;
  Follow2?: string;
  follow3?: string;
  Follow3?: string;
  aiData?: string;
  query?: string;
  date?: string;
  app?: string;
  app_probability?: string;
  indicators?: string;
  dev_opportunities?: string;
  ai_potential?: string;
  bde_recommendation?: string;
  source_urls?: string;
  full_response?: string;
  response_file?: string;
  SubjectData?: string;
  EmailData?: string;
  LinkedInData?: string;
  SubjectF1?: string;
  SubjectF2?: string;
  SubjectF3?: string;
  SubjectF4?: string;
};

const CampaignDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    const fetchOne = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const res = await ApiService({
          method: "GET",
          endpoint: `${apiPaths.campaignEdit}${id}`,
        });
        // Try common response shapes
        const item = (res && (res.data || res.item || res)) as any;
        setData(item || null);
      } catch (err: any) {
        setError(err?.message || "Failed to load details");
      } finally {
        setLoading(false);
      }
    };
    fetchOne();
  }, [id]);

  const normalized = useMemo(() => {
    if (!data) return null;
    const follow1 = (data.follow1 ?? data.Follow1) || "";
    const follow2 = (data.follow2 ?? data.Follow2) || "";
    const follow3 = (data.follow3 ?? data.Follow3) || "";
    // Subject fallbacks (prefer explicit fields, then payload/custom_variables)
    const payload: any = (data as any)?.payload || {};
    const cv: any = payload?.custom_variables || {};
    const SubjectData =
      data.SubjectData || payload.email_subject_f || cv.email_subject_f || "";
    const SubjectF1 =
      (data as any).SubjectF1 || cv.email_subject_f1 || payload.email_subject_f1 || "";
    const SubjectF2 =
      (data as any).SubjectF2 || cv.email_subject_f2 || payload.email_subject_f2 || "";
    const SubjectF3 =
      (data as any).SubjectF3 || cv.email_subject_f3 || payload.email_subject_f3 || "";
    const SubjectF4 =
      (data as any).SubjectF4 || cv.email_subject_f4 || payload.email_subject_f4 || "";

    const campaignRef =
      typeof data.campaignId === "object"
        ? (data.campaignId as any)?._id
        : data.campaignId;
    return {
      ...data,
      follow1,
      follow2,
      follow3,
      campaignId: campaignRef,
      SubjectData,
      SubjectF1,
      SubjectF2,
      SubjectF3,
      SubjectF4,
    } as Campaign;
  }, [data]);

  if (loading) {
    return (
      <div className="m-5">
        <button
          type="button"
          className="px-3 py-1 border rounded"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
        <div className="mt-4">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-5">
        <button
          type="button"
          className="px-3 py-1 border rounded"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
        <div className="mt-4 text-red-600 text-sm">{error}</div>
      </div>
    );
  }

  if (!normalized) {
    return (
      <div className="m-5">
        <button
          type="button"
          className="px-3 py-1 border rounded"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
        <div className="mt-4">No data found.</div>
      </div>
    );
  }

  return (
    <div className="m-5 flex flex-col gap-4 h-[80vh]">
      <div className="flex items-center gap-4">
        <button
          type="button"
          className="px-3 py-1 border rounded"
          onClick={() => navigate(-1)}
        >
          <MoveLeft />
        </button>
        <h2 className="text-lg font-semibold">Campaign Details</h2>
      </div>

      <div className="bg-white overflow-y-auto  rounded-lg shadow p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <Detail label="Date" value={normalized.date} />
        <Detail label="Lead Name" value={normalized.leadName} />
        <Detail label="Company" value={normalized.companyName} />
        <Detail label="Email" value={normalized.email} />
        <Detail label="Lead Status" value={normalized.leadStatus} />
        <Detail label="LinkedIn Status" value={normalized.linkedInLeadStatus} />
        <Detail label="Website" value={normalized.companyWebsite} isLink />
        <Detail label="LinkedIn" value={normalized.linkedInLink} isLink />
        <Detail label="Lead Link" value={normalized.leadWebLink} isLink />
        <Detail label="Instagram" value={normalized.instagramLink} isLink />
        <Detail label="Query" value={normalized.query} multiline />
        <Detail label="Description" value={normalized.description} multiline />
        <Detail label="App" value={normalized.app} />
        <Detail label="App Probability" value={normalized.app_probability} />
        <Detail label="Indicators" value={normalized.indicators} multiline />
        <Detail label="Dev Opportunities" value={normalized.dev_opportunities} multiline />
        <Detail label="AI Potential" value={normalized.ai_potential} multiline />
        <Detail label="BDE Recommendation" value={normalized.bde_recommendation} multiline />
        <Detail label="Source URLs" value={normalized.source_urls} multiline />
        {/* <Detail label="Full Response" value={normalized.full_response} multiline /> */}
        <Detail label="Response File" value={normalized.response_file} isLink />
        <Detail label="Subject Data" value={normalized.SubjectData} multiline />
        <Detail label="Subject F1" value={normalized.SubjectF1} multiline />
        <Detail label="Subject F2" value={normalized.SubjectF2} multiline />
        <Detail label="Subject F3" value={normalized.SubjectF3} multiline />
        <Detail label="Subject F4" value={normalized.SubjectF4} multiline />
        <Detail label="Email Data" value={normalized.EmailData} multiline />
        <Detail label="LinkedIn Data" value={normalized.LinkedInData} multiline />
        <Detail
          label="Personalization"
          value={normalized.personalization}
          multiline
        />
        <Detail label="Follow 1" value={normalized.follow1} multiline />
        <Detail label="Follow 2" value={normalized.follow2} multiline />
        <Detail label="Follow 3" value={normalized.follow3} multiline />
        <div onClick={() => setIsOpen(true)} className="cursor-pointer">
          <Detail label="AI Data" value={normalized.aiData} multiline />
        </div>
      </div>
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        style="max-width: 48rem; width: 100%; max-height: 80vh;"
      >
        <h3 className="text-lg font-semibold mb-3">AI Data</h3>
        <div className="text-sm whitespace-pre-wrap break-words">
          {normalized.aiData || "-"}
        </div>
      </Modal>
    </div>
  );
};

const Detail = ({
  label,
  value,
  isLink = false,
  multiline = false,
}: {
  label: string;
  value?: string | null;
  isLink?: boolean;
  multiline?: boolean;
}) => {
  const display = (value ?? "").toString().trim();
  if (!display)return null;

  if (isLink) {
    return (
      <div className="flex flex-col">
        <span className="text-[12px] text-gray-500">{label}</span>
        <a
          className="text-sm text-blue-600 border-b border-blue-600 w-fit"
          href={display}
          target="_blank"
          rel="noopener noreferrer"
        >
          {display}
        </a>
      </div>
    );
  }

  if (multiline) {
    return (
      <div className="flex flex-col">
        <span className="text-[12px] text-gray-500">{label}</span>
        <pre className="text-sm whitespace-pre-wrap break-words">{display}</pre>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <span className="text-[12px] text-gray-500">{label}</span>
      <span className="text-sm break-words">{display}</span>
    </div>
  );
};

export default CampaignDetails;
